import { IonImg, IonText } from '@ionic/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { mergeClassNames } from '@/utils';

import { CountdownImages } from '@/assets/countdown';

import styles from './CalibrationCountdown.module.css';

interface CalibrationCountdownProps {
  isPlaying: boolean;
}

export const CalibrationCountdown = ({ isPlaying }: CalibrationCountdownProps) => {
  const [active, setActive] = useState<number | 'full'>(88);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const images = useMemo(() => {
    return Array(89)
      .fill(0)
      .map((_, i) => i);
  }, []);

  useEffect(() => {
    console.log(active);
  }, [active]);

  /**
   * Clears the specified interval if it is not null and resets the interval reference.
   *
   * @param interval - The interval to be cleared, which can be a NodeJS.Timeout or null.
   */
  function nullableClearInterval(interval: NodeJS.Timeout | null) {
    if (interval) {
      clearInterval(interval);
      intervalRef.current = null;
    }
  }

  /**
   * Manages the countdown timer based on the `isPlaying` state.
   *
   * If `isPlaying` is true, sets an interval to decrement the `active` state
   * every 110 milliseconds. When `active` reaches 0, the interval is cleared
   * and `active` is set to 'full'. If `active` is 'full', the interval is also cleared.
   *
   * If `isPlaying` is false, clears the interval and any existing timeout.
   */
  useEffect(() => {
    if (isPlaying) {
      // Count down from 10 to 0, we go through all active states
      intervalRef.current = setInterval(() => {
        setActive((prev) => {
          // If the current state is 0 or 'full', we are done
          if (prev === 0 || prev === 'full') {
            nullableClearInterval(intervalRef.current);
            return 'full';
          }

          return prev - 1;
        });
      }, 110);
    } else {
      nullableClearInterval(intervalRef.current);
    }

    return () => {
      nullableClearInterval(intervalRef.current);
    };
  }, [isPlaying]);

  /**
   * Calculates the remaining time based on the current active state.
   *
   * Uses a memoized function to determine the remaining time by comparing
   * the current active state to a maximum index and a total time. If the
   * active state is 'full', it returns 0. Otherwise, it calculates the
   * remaining time as a proportion of the total time, rounded up to the
   * nearest integer. If the calculated remaining time is 0, it returns '1'.
   */
  const time = useMemo(() => {
    if (active === 'full') {
      return 0;
    }

    const maxIndex = 88;
    const totalTime = 10;

    const remainingTime = Math.ceil((active / maxIndex) * totalTime);

    if (remainingTime === 0) {
      return '1';
    }

    return remainingTime;
  }, [active]);

  return (
    <div className={styles.wrapper}>
      {images.map((image) => (
        <IonImg
          className={mergeClassNames(styles.countdown, active === image && styles.active)}
          key={image}
          src={CountdownImages.get(image)}
        />
      ))}

      <IonImg
        className={mergeClassNames(styles.countdown, active === 'full' && styles.active)}
        src={CountdownImages.get('full')}
      />

      <IonText className={styles.time}>{time}</IonText>
    </div>
  );
};
